import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  CreditCard, 
  Crown, 
  Euro,
  Save,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

interface PricingPlan {
  id: string;
  plan: string;
  prix: number;
  devise: string;
  description: string | null;
  actif: boolean;
  created_at: string;
  updated_at: string;
}

export const PricingConfig = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedPlans, setEditedPlans] = useState<Record<string, Partial<PricingPlan>>>({});

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('configuration_prix')
        .select('*')
        .order('plan', { ascending: true });

      if (error) throw error;

      setPlans(data || []);
      setEditedPlans({});
    } catch (error) {
      console.error('Erreur lors du chargement des prix:', error);
      toast.error('Erreur lors du chargement de la configuration des prix');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (planId: string, field: keyof PricingPlan, value: any) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: value
      }
    }));
  };

  const getEditedValue = (plan: PricingPlan, field: keyof PricingPlan) => {
    if (editedPlans[plan.id] && editedPlans[plan.id][field] !== undefined) {
      return editedPlans[plan.id][field];
    }
    return plan[field];
  };

  const hasChanges = Object.keys(editedPlans).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      for (const [planId, changes] of Object.entries(editedPlans)) {
        const { error } = await supabase
          .from('configuration_prix')
          .update(changes)
          .eq('id', planId);

        if (error) throw error;
      }

      toast.success('Prix mis à jour avec succès');
      fetchPricing();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde des prix');
    } finally {
      setSaving(false);
    }
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'annuel':
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 'mensuel':
        return <CreditCard className="h-6 w-6 text-blue-500" />;
      default:
        return <Euro className="h-6 w-6 text-gray-500" />;
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'annuel':
        return 'Abonnement Annuel';
      case 'mensuel':
        return 'Abonnement Mensuel';
      default:
        return plan.charAt(0).toUpperCase() + plan.slice(1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Chargement de la configuration des prix...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-nautic">Configuration des prix Stripe</h2>
          <p className="text-muted-foreground">Gérez les tarifs des abonnements affichés aux utilisateurs</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchPricing}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-nautic-gold hover:bg-nautic-gold/90 text-nautic-dark"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className={!getEditedValue(plan, 'actif') ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getPlanIcon(plan.plan)}
                  <div>
                    <CardTitle>{getPlanLabel(plan.plan)}</CardTitle>
                    <CardDescription>
                      Dernière modification: {new Date(plan.updated_at).toLocaleDateString('fr-FR')}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor={`actif-${plan.id}`} className="text-sm text-muted-foreground">
                    Actif
                  </Label>
                  <Switch
                    id={`actif-${plan.id}`}
                    checked={getEditedValue(plan, 'actif') as boolean}
                    onCheckedChange={(checked) => handlePriceChange(plan.id, 'actif', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`prix-${plan.id}`}>Prix</Label>
                  <div className="relative">
                    <Input
                      id={`prix-${plan.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={getEditedValue(plan, 'prix') as number}
                      onChange={(e) => handlePriceChange(plan.id, 'prix', parseFloat(e.target.value))}
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      €
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`devise-${plan.id}`}>Devise</Label>
                  <Input
                    id={`devise-${plan.id}`}
                    value={getEditedValue(plan, 'devise') as string}
                    onChange={(e) => handlePriceChange(plan.id, 'devise', e.target.value)}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`description-${plan.id}`}>Description</Label>
                <Textarea
                  id={`description-${plan.id}`}
                  value={(getEditedValue(plan, 'description') as string) || ''}
                  onChange={(e) => handlePriceChange(plan.id, 'description', e.target.value)}
                  placeholder="Description affichée aux utilisateurs"
                  rows={2}
                />
              </div>

              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Aperçu du prix affiché:</span>
                  <span className="font-bold text-lg">
                    {(getEditedValue(plan, 'prix') as number).toFixed(2)} €
                    <span className="text-sm font-normal text-muted-foreground">
                      /{plan.plan === 'annuel' ? 'an' : 'mois'}
                    </span>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-300 rounded-lg p-4 shadow-lg">
          <p className="text-yellow-800 text-sm">
            Vous avez des modifications non enregistrées
          </p>
        </div>
      )}
    </div>
  );
};
